import axios from "axios"
import { apiClient } from "@/services/api"
import { i18n } from "@/plugins/i18n"

/**
 * Hands [blob] to the browser as a download called [filename].
 *
 * Lifted out of [downloadFileFromXHR] once a second kind of caller appeared: a file the SPA builds
 * itself has no response to read a name off, but the anchor dance and the deferred revoke below are
 * the same either way, and are the part worth having exactly one copy of.
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // The blob stays alive for as long as its URL does, so without this every download leaks its own
  // bytes for the lifetime of the page. Deferred rather than immediate: revoking in the same tick can
  // beat the click through to the browser's download handler.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * Downloads the file of the XHR to the client.
 * XHR responseType needs to be set to 'blob'.
 */
export const downloadFileFromXHR = (data, headers, defaultFileName = "file.pdf") => {
  let filename = defaultFileName
  const disposition = headers["content-disposition"]
  if (disposition && disposition.indexOf("attachment") !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)
    if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, "")
  }
  downloadBlob(new Blob([data], { type: "application/octet-stream" }), filename)
}

// ------------------------------------------------------------------------------------------------
// CSV written in the browser.
//
// Everything the product exports is written by the server, and should stay that way: an export is a
// claim about stored data and the server is what holds it. The exception is a table the SPA already
// has in full and the API has no route for — the Arbeitszeitkonto — where a file assembled here is
// the same figures the reviewer just read, and a second server query would be a chance for the two
// to disagree.
//
// What must not diverge is the *format*. These constants are the SPA's copy of the backend's
// `CsvDialect` / `CsvFormatFactory`, so a locally built file and a server-built one open identically
// in the same Excel: GERMAN is `;`-separated UTF-8 **with a BOM** and a decimal comma, TECHNICAL is
// `,`-separated UTF-8 without one. Both use CRLF and quote the way Apache Commons CSV does, because
// that is what the server's printer emits.

export const CSV_DIALECTS = {
  GERMAN: { delimiter: ";", bom: true, decimalComma: true },
  TECHNICAL: { delimiter: ",", bom: false, decimalComma: false },
}

const dialectSpec = dialect => CSV_DIALECTS[dialect] ?? CSV_DIALECTS.GERMAN

/**
 * A decimal-hours figure as the dialect spells it — `8.25` stays `8.25`, or becomes `8,25`.
 *
 * Null and empty stay **empty**, which is the whole reason this is not `Number(...).toFixed(2)`: an
 * account with no seed has no `openingBalanceHours`, and "no opening balance" is a different claim
 * from "an opening balance of zero". The value arrives from the API already rounded to two places,
 * so it is re-spelled rather than re-computed.
 */
export const csvDecimal = (value, dialect) => {
  if (value === null || value === undefined || value === "") return ""
  const text = String(value)
  return dialectSpec(dialect).decimalComma ? text.replace(".", ",") : text
}

/**
 * One cell, quoted the way Commons CSV's `QuoteMode.MINIMAL` — the server printer's default — quotes
 * it: only where a bare value would not survive the round trip.
 *
 * The two odd-looking rules are the parser's, not ours. A leading character below `#` or a trailing
 * one below a space gets quoted because Commons CSV's own reader trips over them, and an empty cell
 * is quoted only when it opens a record, so that a line of nothing still parses as one field rather
 * than as no line at all.
 */
const csvCell = (value, delimiter, firstInRecord) => {
  const text = value === null || value === undefined ? "" : String(value)
  if (text === "") return firstInRecord ? '""' : ""
  const quote =
    text.charCodeAt(0) <= 0x23 ||
    text.charCodeAt(text.length - 1) <= 0x20 ||
    text.includes(delimiter) ||
    text.includes('"') ||
    text.includes("\n") ||
    text.includes("\r")
  return quote ? `"${text.replace(/"/g, '""')}"` : text
}

/** The bytes of a CSV in [dialect], BOM included where the dialect wants one. */
export const buildCsvBlob = (dialect, header, rows) => {
  const { delimiter, bom } = dialectSpec(dialect)
  // CRLF, and one after the final record too: `CSVPrinter.printRecord` terminates every row it
  // writes, so a file that ended bare would differ from the server's in its last byte.
  const line = cells => `${cells.map((cell, index) => csvCell(cell, delimiter, index === 0)).join(delimiter)}\r\n`
  const text = line(header) + rows.map(line).join("")
  return new Blob([bom ? `\ufeff${text}` : text], { type: "text/csv;charset=utf-8" })
}

/** [buildCsvBlob] straight to the client — the local-blob sibling of [downloadFileFromXHR]. */
export const downloadCsv = ({ dialect, header, rows, filename }) =>
  downloadBlob(buildCsvBlob(dialect, header, rows), filename)

/**
 * Byte count as something a person reads, binary units.
 *
 * Lifted out of `LewSettingsDataExportTab`, which had the only copy — the file browser is the second
 * screen to need one, and a second private copy is how two screens start disagreeing about what a
 * megabyte is.
 *
 * @param bytes may be null/undefined: a file stored before the API reported sizes has none.
 */
export const formatFileSize = bytes => {
  if (bytes === null || bytes === undefined) return "—"
  const units = ["B", "KiB", "MiB", "GiB", "TiB"]
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit++
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

// Content type -> icon. Everything in the product used to render `mdi-file` regardless, which is fine
// in a list of one entity's attachments and useless in a browser listing every type at once: the icon
// is the only thing that distinguishes a photo from a contract before you read the name.
//
// Matched most-specific first, so `application/vnd...sheet` beats the `application/` fallback.
// Distinct *glyphs*, not the `mdi-file-*` family: at 20px every icon in that family is the same
// sheet-of-paper silhouette with a small mark on it, so a column of them carries no information at
// the size it is actually rendered. A picture, a table and an archive should be tellable apart at a
// glance, which is the only reason this column exists.
const FILE_TYPE_ICONS = [
  [/^image\//, "mdi-image-outline"],
  [/^video\//, "mdi-video-outline"],
  [/^audio\//, "mdi-music-note-outline"],
  [/^application\/pdf$/, "mdi-file-pdf-box"],
  [/^text\/csv$/, "mdi-table"],
  [/(spreadsheet|ms-?excel)/, "mdi-file-excel-outline"],
  [/(presentation|powerpoint)/, "mdi-file-powerpoint-outline"],
  [/(wordprocessing|ms-?word)/, "mdi-file-word-outline"],
  [/(zip|compressed|x-tar|x-7z|rar)/, "mdi-folder-zip-outline"],
  [/^text\//, "mdi-text-box-outline"],
]

/** @returns an `mdi-*` name; never null, so a row always has something to draw. */
export const fileTypeIcon = contentType => {
  if (!contentType) return "mdi-file-outline"
  const match = FILE_TYPE_ICONS.find(([pattern]) => pattern.test(contentType))
  return match ? match[1] : "mdi-file-outline"
}

/** The content types `LewViewFileModal` can actually render inline. */
export const VIEWABLE_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"]

export const isViewable = contentType => VIEWABLE_CONTENT_TYPES.includes(contentType)

/**
 * The type of what a `/file/view` or `/user/picture` response actually carries, else [fallback].
 *
 * The server shrinks large pictures and PDFs for viewing, and a PNG photo can come back as a JPEG, so
 * the stored type no longer has to match the bytes. A server from before that answered
 * `application/octet-stream` for everything; that says nothing, so the stored type is used instead.
 */
export const blobTypeOf = (response, fallback) => {
  const type = response?.headers?.["content-type"]?.split(";")[0].trim()
  return type && type !== "application/octet-stream" ? type : fallback
}

/** The content types a Bautagesbericht's or Stundenlohnbericht's PDF can embed (an attachment's `visible`). */
export const PRINTABLE_CONTENT_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/gif"]

export const isPrintable = contentType => PRINTABLE_CONTENT_TYPES.includes(contentType)

/**
 * The FilePond item holding [file], or undefined when the pond no longer has it.
 *
 * FilePond identifies its items by an id of its own; the `file` on each item is the very File object
 * it handed to `server.process`, so reference equality finds the item an upload belongs to.
 */
const pondItemFor = (pond, file) => (pond?.getFiles?.() ?? []).find(item => item.file === file)

/**
 * Short label for a failed upload, shown by FilePond next to the file that failed.
 *
 * Deliberately terse: the response interceptor already raises a snackbar spelling the failure out
 * (including what the server refused, or that the outcome is unknown), and this sits in a row a few
 * centimetres wide. It only has to mark which file did not make it.
 *
 * A refusal and a silence are worth telling apart, because only one of them is settled. `uploadFailed`
 * means the server said no. `uploadNoAnswer` means nothing came back, which is not the same as "not
 * uploaded" — the file may be stored with only the answer lost — so the label does not claim it is.
 */
const uploadErrorLabel = error =>
  i18n.global.t(
    error.response ? "general.component.file.pond.uploadFailed" : "general.component.file.pond.uploadNoAnswer"
  )

/**
 * One key per file picked, stable across every attempt at uploading it.
 *
 * The server answers a repeat of a key with the file the first attempt made, instead of storing a
 * second copy — which is what makes the retry button safe after a failure whose outcome is unknown.
 * So the key has to survive a retry and must not survive anything else: keyed on the File object,
 * because FilePond hands `server.process` the same one on every attempt at an item and a different
 * one for every other item, including the same file picked again on purpose.
 *
 * A WeakMap so a removed file takes its key with it; `randomUUID` because the key has to be unique
 * across everybody uploading to the same record, not just within this pond.
 *
 * The one thing this rests on: what reaches `server.process` is the output of FilePond's
 * `PREPARE_OUTPUT` filter chain, which returns the item's own File untouched only because no plugin we
 * register hooks it. A transform plugin — image resizing is the usual one — would hand out a freshly
 * built Blob per attempt, and every retry would look like a new upload again. `pondItemFor` matches on
 * the same reference and would break in the same breath, which is what the warning there is for.
 */
const uploadKeys = new WeakMap()

const uploadKeyFor = file => {
  const existing = uploadKeys.get(file)
  if (existing) return existing
  // Absent on http:// origins, where a collision costs a rejected duplicate at worst.
  const key = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  uploadKeys.set(file, key)
  return key
}

/**
 * Runs one FilePond upload against [url] and keeps the pond in step with the outcome.
 *
 * [event] is the payload `LewFilePond`/`LewHwrFilePond` emit from `server.process`; [onUploaded] receives the
 * created file so the caller can put it in its own list.
 *
 * Every entity's file tab had its own copy of this, and every copy tracked the pond with a counter of
 * failed uploads (`pond.removeFile(0 + errorUploadCount)`) — the idea being that failed items stay at
 * the front of the pond, so the one that just succeeded sits that many places in. It does not survive
 * the retry button: retrying the same file counts again, so after two failures and a successful third
 * attempt the index points past the end and the file is left in the pond as well as in the table, and
 * every later upload in that session removes somebody else's row. Cancelling counted too, which threw
 * the index off without any failure at all. Removing the item by its own id needs no bookkeeping.
 */
export const uploadPondFile = ({ url, event, onUploaded }) => {
  const { cancelTokenSource, formData, progress, load, error, pond, file } = event
  return apiClient
    .post(url, formData, {
      cancelToken: cancelTokenSource.token,
      onUploadProgress: progress,
      headers: {
        tenant: true,
        authorization: true,
        "Content-Type": "multipart/form-data",
        "X-Upload-Key": uploadKeyFor(file),
      },
    })
    .then(response => {
      const newFile = response.data
      load(newFile.id)

      const item = pondItemFor(pond, file)
      // No match means the File that came back is not the one the pond is holding — see the note on
      // `uploadKeys`. Worth saying out loud: the symptom is an uploaded file staying in the pond, and
      // the same cause quietly turns every retry into a duplicate.
      if (item) pond.removeFile(item.id)
      else console.warn("Uploaded file is not in the pond by reference; upload keys are unreliable", url)

      onUploaded(newFile)
      return newFile
    })
    .catch(e => {
      // Cancelling is the user tapping the cancel button; FilePond has already reset the item.
      if (axios.isCancel(e)) return
      // Named, so the Sentry breadcrumb says which upload died rather than "Request failed".
      console.warn("File upload failed", url, e)
      error(uploadErrorLabel(e))
    })
}
